import React, { useState } from 'react';
import {
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Divider,
    Typography,
    Box
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VideocamIcon from '@mui/icons-material/Videocam';
import LanguageIcon from '@mui/icons-material/Language';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

const PopupLinksDropdown = ({ host, hostType = 'default' }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const getLinks = () => {
        switch (hostType) {
            case 'crossroad':
                let links = [];
                if (host.child) {
                    host.child.forEach((child) => {
                        if (child.scripts) {
                            const webScripts = child.scripts.filter(script =>
                                String(script.type) === '6'
                            ).map(script => ({
                                id: script.url,
                                name: child.name.split(' / ')[1],
                                url: script.url,
                                type: 'web'
                            }));
                            links = [...links, ...webScripts];
                        }
                    });
                }
                links.sort((a ,b) => (a.name.localeCompare(b.name)))
                return links;

            case 'speed':
                if (host.interfaces) {
                    return host.interfaces.filter(iface => String(iface.type) === '1').map(iface => ({
                        id: iface.ip,
                        name: iface.ip,
                        url: 'http://' + iface.ip,
                        type: 'web'
                    }));
                }
                return [];

            case 'vgk':
                let vgkLinks = [];
                const web_links = host.macros.find(macro => macro.macro.includes("WEB_LINKS"));
                const ip_main = host.interfaces.find((i) => i.type === '1' && i.ip.includes('10.8.'))?.ip;
                const ip_sec = host.interfaces.find((i) => i.type === '1' && i.ip.includes('10.2.'))?.ip;
                let values = '';
                let descriptions = '';
                if (web_links) {
                    values = web_links.value.split(',');
                    descriptions = web_links.description.split(',');
                }
                
                values && values.forEach && values.forEach((link) => {
                    const port = host.macros.find(macro => macro.macro.includes(`{\$${link.toUpperCase()}}`))?.value;
                    const descr = descriptions[values.indexOf(link)];
                    const url = `http://${port?.includes('080') ? ip_sec : ip_main}:${port}`;
                    vgkLinks.push({
                        id: link,
                        name: descr ? descr : link,
                        url: url,
                        type: 'web'
                    });
                });
                return vgkLinks;

            default:
                if (host.scripts) {
                    return host.scripts.filter(script =>
                        String(script.type) === 'web' || String(script.type) === 'url'
                    );
                }
                return [];
        }
    };

    const links = getLinks();

    if (!links || links.length === 0) {
        return null;
    }

    const handleLinkClick = (link) => {
        let url = link.url || link.value;
        if (url) {
            window.open(url, '_blank');
        }
        handleClose();
    };

    const getLinkIcon = (link) => {
        const name = (link.name || link.type || '').toLowerCase();

        if (name.includes('camera') || name.includes('камера') || name.includes('видео')) {
            return <VideocamIcon sx={{ fontSize: { xs: '14px', md: '16px' }, color: 'var(--primary-color)' }} />;
        }

        if (name.includes('web') || name.includes('веб') || name.includes('интерфейс')) {
            return <LanguageIcon sx={{ fontSize: { xs: '14px', md: '16px' }, color: 'var(--success-color)' }} />;
        }

        if (name.includes('admin') || name.includes('админ') || name.includes('управление')) {
            return <AdminPanelSettingsIcon sx={{ fontSize: { xs: '14px', md: '16px' }, color: 'var(--warning-color)' }} />;
        }

        return <OpenInNewIcon sx={{ fontSize: { xs: '14px', md: '16px' }, color: 'var(--info-color)' }} />;
    };

    return (
        <>
            <Tooltip title="Внешние ссылки" arrow placement="top">
                <Box
                    onClick={handleClick}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: '3px', md: '4px' },
                        padding: { xs: '3px 6px', md: '4px 8px' },
                        borderRadius: { xs: '4px', md: '6px' },
                        backgroundColor: 'var(--background-paper)',
                        border: '1px solid var(--divider-color)',
                        cursor: 'pointer',
                        transition: 'all 150ms ease',
                        '&:hover': {
                            backgroundColor: 'var(--success-color)',
                            borderColor: 'var(--success-color)',
                            '& .btn-icon, & .btn-label': {
                                color: 'white',
                            },
                            transform: 'translateY(-1px)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        },
                        '&:active': {
                            transform: 'translateY(0)',
                        }
                    }}
                >
                    <LinkIcon 
                        className="btn-icon" 
                        sx={{ 
                            fontSize: { xs: '12px', md: '14px' }, 
                            color: 'var(--success-color)',
                            transition: 'color 150ms'
                        }} 
                    />
                    <Typography 
                        className="btn-label"
                        sx={{ 
                            fontSize: { xs: '9px', md: '10px' }, 
                            fontWeight: 600, 
                            color: 'var(--success-color)',
                            whiteSpace: 'nowrap',
                            transition: 'color 150ms'
                        }}
                    >
                        Ссылки
                    </Typography>
                </Box>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        sx: {
                            minWidth: { xs: '140px', md: '160px' },
                            maxWidth: { xs: '280px', md: '320px' },
                            borderRadius: { xs: '6px', md: '8px' },
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            border: '1px solid var(--divider-color)',
                            backgroundColor: 'var(--background-paper)',
                            '& .MuiList-root': {
                                padding: { xs: '3px', md: '4px' },
                            }
                        }
                    }
                }}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            >
                <Typography
                    sx={{
                        px: { xs: 1, md: 1.5 },
                        py: { xs: 0.4, md: 0.5 },
                        fontSize: { xs: '9px', md: '10px' },
                        fontWeight: 600,
                        color: 'var(--text-secondary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}
                >
                    Внешние ссылки
                </Typography>
                <Divider sx={{ my: { xs: 0.4, md: 0.5 } }} />
                {links.map((link, index) => (
                    <MenuItem
                        key={link.id || index}
                        onClick={() => handleLinkClick(link)}
                        sx={{
                            borderRadius: { xs: '3px', md: '4px' },
                            py: { xs: 0.5, md: 0.75 },
                            px: { xs: 0.75, md: 1 },
                            minHeight: 'auto',
                            '&:hover': {
                                backgroundColor: 'var(--background-default)',
                            },
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: { xs: '24px', md: '28px' } }}>
                            {getLinkIcon(link)}
                        </ListItemIcon>
                        <ListItemText
                            primary={link.name || link.macro || `Ссылка ${index + 1}`}
                            slotProps={{
                                primary: {
                                    sx: {
                                        fontSize: { xs: '11px', md: '12px' },
                                        fontWeight: 500,
                                        color: 'var(--text-primary)'
                                    }
                                }
                            }}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default PopupLinksDropdown;
